import * as React from 'react';
import { fieldValidation, LinkReference, ReferenceLink } from '../../amorg-common';
import { getReferenceLinks, setReferenceLinks } from '../../api/referenceLinkClientApi';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../designSystem/formField/text/TextField';
import { DarkLink } from '../../designSystem/Link';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../MessageCard';
import { Modal } from '../../designSystem/Modal';
import { Small, Span14 } from '../../designSystem/typography/Body';
import { Button } from '../button/Button';
import { Card } from '../Card';
import { colors } from '../colors/color';
import { Label } from '../Label';
import { ListItem } from '../List/ListItem';

type ReferenceLinksPanelProps = 
{
    readOnly?: boolean,
    reference:LinkReference,
    /** Use false inside a modal. Deletion confirmation is always inline. */
    modalEditing:boolean,
    hide?:boolean,
    onGetLinksCount?: (count:number) => void
}

export function ReferenceLinksPanel(props:ReferenceLinksPanelProps):JSX.Element
{
    const [loading, setLoading] = React.useState<boolean>(false);
    const [links, setLinks] = React.useState<ReferenceLink[]>([]);

    React.useEffect(() => {
        const getData = async () => {
            setLoading(true);
            const data = await getReferenceLinks(props.reference.id, props.reference.type);
            if(data !== null)
                setLinks(data.links);
            props.onGetLinksCount?.(data?.links.length || 0);
            setLoading(false);
        }
        getData();
    }, [props.reference.id, props.reference.type]);

    async function handleUpdate(links:ReferenceLink[]):Promise<ReferenceLinksUpdateResult>
    {
        setLoading(true);
        const result = await setReferenceLinks(props.reference, links);
        if(!result.isSuccess){
            setLoading(false);
            return { errorMessage:'Der opstod en uventet fejl. Prøv igen eller kontakt support' };
        }
        else {
            const data = await getReferenceLinks(props.reference.id, props.reference.type);
            setLinks(data === null ? [] : data.links);
            props.onGetLinksCount?.(data?.links.length || 0);
            setLoading(false);
            return { };
        }
    }
 
    function handleDeleteConfirmation(linkIndex:number):Promise<ReferenceLinksUpdateResult>
    {
        return handleUpdate(links.filter((link, index) => index !== linkIndex));
    }

    const editingPanelProps:ReferenceLinksEditingPanelProps = {
        readOnly: props.readOnly,
        links,
        loading,
        hide: props.hide,
        onUpdate: handleUpdate,
        onDeleteConfirmation: handleDeleteConfirmation
    };

    return props.modalEditing
        ? <ModalEditingReferenceLinksPanel {...editingPanelProps} />
        : <InlineEditingReferenceLinksPanel {...editingPanelProps} />;
}

type ReferenceLinksUpdateResult = { errorMessage?:string }

type ReferenceLinksEditingPanelProps = {
    readOnly?: boolean,
    links:ReferenceLink[],
    loading:boolean,
    hide?:boolean,
    onUpdate:(links:ReferenceLink[]) => Promise<ReferenceLinksUpdateResult>,
    onDeleteConfirmation:(linkIndex:number) => Promise<ReferenceLinksUpdateResult>
}

type ReferenceLinkEditorState = {
    link:ReferenceLink,
    linkIndex:number | null,
    hideValidation:boolean,
    errorMessage?:string
}

function useReferenceLinkEditor(props:ReferenceLinksEditingPanelProps)
{
    const [editor, setEditor] = React.useState<ReferenceLinkEditorState | null>(null);

    function startCreation():void
    {
        setEditor({
            link: { title:'', description:'', url:'https://', iconName:null },
            linkIndex: null,
            hideValidation: true
        });
    }

    function startEditing(linkIndex:number):void
    {
        setEditor({ link:props.links[linkIndex], linkIndex, hideValidation:true });
    }

    function handleChange(link:ReferenceLink):void
    {
        if(editor)
            setEditor({ ...editor, link });
    }

    function handleCancel():void
    {
        if(!props.loading)
            setEditor(null);
    }

    async function handleSubmit():Promise<void>
    {
        if(!editor || props.loading)
            return;

        const valid = fieldValidation.referenceLink.title(editor.link.title) &&
            fieldValidation.referenceLink.description(editor.link.description) &&
            fieldValidation.referenceLink.url(editor.link.url);
        if(!valid)
        {
            setEditor({ ...editor, hideValidation:false, errorMessage:undefined });
            return;
        }

        const updatedCollection = editor.linkIndex === null
            ? [...props.links, editor.link]
            : props.links.map((link, index) => index === editor.linkIndex ? editor.link : link);

        const result = await props.onUpdate(updatedCollection);
        if(result.errorMessage)
        {
            setEditor({ ...editor, hideValidation:false, errorMessage:result.errorMessage });
        }
        else
            setEditor(null);
    }

    const editorProps:LinkEditorProps | null = editor ? {
        link: editor.link,
        hideValidation: editor.hideValidation,
        errorMessage: editor.errorMessage,
        isCreate: editor.linkIndex === null,
        loading: props.loading,
        onChange: handleChange,
        onSubmit: handleSubmit,
        onCancel: handleCancel
    } : null;

    return { startCreation, startEditing, editorProps };
}

function InlineEditingReferenceLinksPanel(props:ReferenceLinksEditingPanelProps):JSX.Element
{
    const editor = useReferenceLinkEditor(props);

    if(props.hide)
        return <></>;

    return (
        <Flex flexDirection="column" justifyContent="stretch" grow="1" gap={8} style={{ minWidth:0 }}>
            {props.loading && <Small role="status">Indlæser...</Small>}
            <ReferenceLinksList
                readOnly={props.readOnly}
                links={props.links}
                disabled={props.loading || editor.editorProps !== null}
                onEdit={editor.startEditing}
                onDeleteConfirmation={props.onDeleteConfirmation}
            />
            {!props.readOnly && editor.editorProps &&
                <Card variant="outlined">
                    <Flex flexDirection="column" gap={16}>
                        <Label>{editor.editorProps.isCreate ? "Opret link" : "Opdater link"}</Label>
                        <LinkEditor {...editor.editorProps} />
                    </Flex>
                </Card>
            }
            {!props.readOnly && <Flex justifyContent="flex-end">
                <Button variant="outlined" size="32px" text="Tilføj link" icon="interface_actions_link"
                    disabled={props.loading || editor.editorProps !== null} onClick={editor.startCreation} />
            </Flex>}
        </Flex>
    );
}

function ModalEditingReferenceLinksPanel(props:ReferenceLinksEditingPanelProps):JSX.Element
{
    const editor = useReferenceLinkEditor(props);

    if(props.hide)
        return <></>;

    return (
        <Flex flexDirection="column" justifyContent="stretch" grow="1" gap={8} style={{ minWidth:0 }}>
            <LoadingPageOverlay loading={props.loading} />
            <ReferenceLinksList
                readOnly={props.readOnly}
                links={props.links}
                disabled={props.loading || editor.editorProps !== null}
                onEdit={editor.startEditing}
                onDeleteConfirmation={props.onDeleteConfirmation}
            />
            {!props.readOnly && <Flex justifyContent="flex-end">
                <Button variant="outlined" size="32px" text="Tilføj link" icon="interface_actions_link"
                    disabled={props.loading || editor.editorProps !== null} onClick={editor.startCreation} />
            </Flex>}
            {!props.readOnly && editor.editorProps &&
                <Modal
                    title={editor.editorProps.isCreate ? "Opret link" : "Opdater link"}
                    showModal
                    onCancel={editor.editorProps.onCancel}
                    content={<LinkEditor {...editor.editorProps} />}
                />
            }
        </Flex>
    );
}

type ReferenceLinksListProps = {
    readOnly?: boolean,
    links:ReferenceLink[],
    disabled:boolean,
    onEdit:(linkIndex:number) => void,
    onDeleteConfirmation:(linkIndex:number) => Promise<ReferenceLinksUpdateResult>
}

function ReferenceLinksList(props:ReferenceLinksListProps):JSX.Element
{
    const [deleteLinkIndex, setDeleteLinkIndex] = React.useState<number | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    async function handleDeleteConfirmation(linkIndex:number):Promise<void>
    {
        setErrorMessage('');
        const result = await props.onDeleteConfirmation(linkIndex);
        if(result.errorMessage)
            setErrorMessage(result.errorMessage);
        else
            setDeleteLinkIndex(null);
    }

    return (
        <Flex flexDirection="column" grow="1" gap={4} style={{minWidth:0}}>
            {props.links.map((link, index) => {
                const linkContent =
                    <DarkLink
                        style={{
                            fontSize:'14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        href={
                            (link.url.startsWith('http://') || link.url.startsWith('https://'))
                                ? link.url
                                : `https://${link.url}`}
                        target="_blank">
                        {link.url}
                    </DarkLink>;

                return (
                    <React.Fragment key={index}>
                        <ListItem
                            border
                            padding="6px"
                            leadingIcon='interface_actions_externalLink'
                            leadingIconColor={colors.icon.active}
                            leadingIconBorder={true}
                            primaryContent={
                                <Flex flexDirection="column">
                                    <Span14 fontWeight={400} color="gray">{link.title}</Span14>
                                    <Small>{linkContent}</Small>
                                </Flex>
                            }
                            buttons={props.readOnly ? [] : [
                                { type:'button', icon: 'interface_actions_delete', onClick: () => {
                                    if(!props.disabled)
                                        setDeleteLinkIndex(index);
                                }},
                                { type:'button', icon: 'interface_actions_edit', onClick: () => {
                                    if(!props.disabled)
                                    {
                                        setDeleteLinkIndex(null);
                                        props.onEdit(index);
                                    }
                                }}
                            ]} 
                        />
                        {!props.readOnly && deleteLinkIndex === index &&
                            <Card variant="outlined" padding="8px">
                                <Flex flexDirection="column" gap={8}>
                                    <Small>Er du sikker på at du vil slette linket?</Small>
                                    <Flex justifyContent="flex-end" gap={8}>
                                        <Button variant="text" text="Annuller" disabled={props.disabled}
                                            onClick={() => setDeleteLinkIndex(null)} />
                                        <Button variant="destructive" text="Slet link" icon="interface_actions_delete"
                                            disabled={props.disabled} onClick={() => handleDeleteConfirmation(index)} />
                                    </Flex>
                                </Flex>
                            </Card>
                        }
                    </React.Fragment>
                );
            })}
            {props.links.length === 0 && <Small>Ingen links oprettet</Small>}
            {errorMessage &&
                <Flex flexDirection="row" justifyContent="center" alignItems="center">
                    <MessageCard variant="error" grow={1}>{errorMessage}</MessageCard>
                </Flex>
            }
        </Flex>
    );
}


type LinkEditorProps = {
    link:ReferenceLink,
    hideValidation:boolean,
    errorMessage?:string,
    isCreate:boolean,
    loading:boolean,
    onChange:(referenceLink:ReferenceLink)=>void,
    onSubmit:() => Promise<void>,
    onCancel:()=>void
}

function LinkEditor(props:LinkEditorProps):JSX.Element
{
    return (
        <Flex flexDirection="column" gap={16}>
            {props.errorMessage &&
                <MessageCard variant="error">{props.errorMessage}</MessageCard>
            }
            <TextField
                label="Titel"
                value={props.link.title}
                disabled={props.loading}
                onChange={(value) => props.onChange({ ...props.link, title:value })}
                errorMessage={
                    props.hideValidation || fieldValidation.referenceLink.title(props.link.title)
                        ? ''
                        : `Titel skal udfyldes. Må maks bestå af 100 tegn`
                }
            />
            <TextField
                label="url"
                value={props.link.url}
                disabled={props.loading}
                onChange={(value) => props.onChange({ ...props.link, url:value })}
                errorMessage={
                    props.hideValidation || fieldValidation.referenceLink.url(props.link.url)
                        ? ''
                        : `URL skal udfyldes med en gyldig webaddresse der starter med https:// og er maks 500 tegn (${props.link.url.length})`
                }
            />
            <Flex justifyContent="flex-end" gap={8}>
                <Button variant="text" text="Annuller" disabled={props.loading} onClick={props.onCancel} />
                <Button variant="filled" text={props.isCreate ? "Opret link" : "Opdater link"}
                    disabled={props.loading} onClick={props.onSubmit} />
            </Flex>
        </Flex>
    );
}